import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Globe, CheckCircle, AlertTriangle, ExternalLink, Loader2, X, Crown } from 'lucide-react';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { toast } from 'sonner';

interface CustomDomainConfigurationProps {
  companyId: string;
  company: {
    customDomain?: string;
    customDomainEnabled?: boolean;
    customDomainStatus?: 'pending' | 'verifying' | 'active' | 'error';
  };
  consultingFirm?: {
    id: string;
    name: string;
    subscription_plan?: string;
  };
  accessToken: string;
  onUpdate: () => void;
}

export function CustomDomainConfiguration({
  companyId,
  company,
  consultingFirm,
  accessToken,
  onUpdate
}: CustomDomainConfigurationProps) {
  const [customDomain, setCustomDomain] = useState(company.customDomain || '');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if firm has Enterprise plan
  const isEnterprisePlan = consultingFirm?.subscription_plan === 'enterprise';
  const hasCustomDomain = !!company.customDomain;
  const domainStatus = company.customDomainStatus;

  // Auto-verify every 10 seconds if status is "verifying"
  useEffect(() => {
    if (domainStatus === 'verifying' && hasCustomDomain) {
      const interval = setInterval(() => {
        handleVerifyDomain();
      }, 10000); // Check every 10 seconds

      return () => clearInterval(interval);
    }
  }, [domainStatus, hasCustomDomain]);

  const handleAddDomain = async () => {
    setError(null);
    setSaving(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/custom-domain`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ customDomain: customDomain.toLowerCase().trim() })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to add custom domain');
        toast.error(data.error || 'Failed to add custom domain');
        return;
      }

      toast.success('Custom domain added. Configure DNS records to proceed.');
      onUpdate();
    } catch (err: any) {
      console.error('Add custom domain error:', err);
      setError('Network error. Please try again.');
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!hasCustomDomain) return;

    setVerifying(true);
    setError(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/custom-domain/verify`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to verify domain');
        return;
      }

      if (data.verified) {
        toast.success('Domain verified successfully');
        onUpdate();
      } else {
        // Don't show error toast during auto-checks
        if (!verifying) {
          toast.info('Domain not verified yet. Please check your DNS configuration.');
        }
      }
    } catch (err: any) {
      console.error('Verify domain error:', err);
      setError('Network error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!confirm(`Are you sure you want to remove the custom domain "${company.customDomain}"? Users will no longer be able to access your portal via this domain.`)) {
      return;
    }

    setRemoving(true);
    setError(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/custom-domain`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to remove custom domain');
        toast.error(data.error || 'Failed to remove custom domain');
        return;
      }

      toast.success('Custom domain removed successfully');
      setCustomDomain('');
      onUpdate();
    } catch (err: any) {
      console.error('Remove custom domain error:', err);
      setError('Network error. Please try again.');
      toast.error('Network error. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const getStatusBadge = () => {
    switch (domainStatus) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Active</Badge>;
      case 'verifying':
        return <Badge className="bg-yellow-500"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Verifying</Badge>;
      case 'pending':
        return <Badge className="bg-blue-500"><AlertTriangle className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'error':
        return <Badge className="bg-red-500"><X className="h-3 w-3 mr-1" /> Error</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-purple-600" />
            <CardTitle>Custom Domain</CardTitle>
            <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              Enterprise
            </Badge>
          </div>
          <Globe className="h-8 w-8 text-purple-400" />
        </div>
        <CardDescription>
          Use your own domain for complete white-label branding (e.g., scopify.acmeconsulting.com)
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isEnterprisePlan ? (
          // Upgrade Prompt for Non-Enterprise Plans
          <Alert className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <Crown className="h-4 w-4 text-purple-600" />
            <AlertDescription className="space-y-3">
              <div>
                <p className="font-medium text-purple-900 mb-2">Enterprise Feature</p>
                <p className="text-sm text-purple-700 mb-3">
                  Custom domains are available exclusively with the Enterprise plan. Make PathKit truly yours by hosting it on your own domain.
                </p>
                <div className="bg-white/50 p-3 rounded-lg space-y-2 mb-3">
                  <p className="text-sm font-medium text-purple-900">Benefits:</p>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Complete white-label experience</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Professional branding with your domain</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>SSL certificate included</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Works alongside your subdomain</span>
                    </li>
                  </ul>
                </div>
                <p className="text-xs text-purple-600">
                  Current plan: {consultingFirm?.subscription_plan?.charAt(0).toUpperCase() + consultingFirm?.subscription_plan?.slice(1) || 'Free'}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        ) : !hasCustomDomain ? (
          // Add Custom Domain Form (Enterprise Only)
          <>
            <div className="space-y-2">
              <Label htmlFor="customDomain">Your Custom Domain</Label>
              <div className="flex gap-2">
                <Input
                  id="customDomain"
                  placeholder="scopify.yourdomain.com"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  disabled={saving}
                />
                <Button
                  onClick={handleAddDomain}
                  disabled={!customDomain || saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Domain'
                  )}
                </Button>
              </div>
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-sm space-y-2">
                <div className="space-y-1">
                  <p className="font-medium">Benefits:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Complete white-label experience</li>
                    <li>Professional branding for your clients</li>
                    <li>SSL certificate included</li>
                    <li>Works alongside your subdomain</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          </>
        ) : (
          // Domain Status & Management
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-purple-100">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{company.customDomain}</span>
                      {getStatusBadge()}
                    </div>
                    {domainStatus === 'active' && (
                      <a
                        href={`https://${company.customDomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:underline flex items-center gap-1 mt-1"
                      >
                        Visit your custom domain
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveDomain}
                  disabled={removing}
                >
                  {removing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </>
                  )}
                </Button>
              </div>

              {domainStatus === 'verifying' && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-sm space-y-3">
                    <div>
                      <p className="font-medium mb-2">DNS Configuration Required:</p>
                      <div className="bg-white p-3 rounded border border-yellow-200 space-y-2">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Add this CNAME record to your DNS:</p>
                          <div className="bg-gray-100 p-2 rounded font-mono text-xs">
                            <div><strong>Type:</strong> CNAME</div>
                            <div><strong>Name:</strong> {company.customDomain?.split('.')[0]}</div>
                            <div><strong>Value:</strong> cname.vercel-dns.com</div>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        DNS propagation can take up to 48 hours. We're checking automatically every 10 seconds.
                      </p>
                    </div>
                    <Button
                      onClick={handleVerifyDomain}
                      disabled={verifying}
                      size="sm"
                      variant="outline"
                      className="w-full"
                    >
                      {verifying ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Check Verification Status
                        </>
                      )}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {domainStatus === 'active' && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-sm">
                    <p className="font-medium">Your custom domain is active.</p>
                    <p className="mt-1">
                      Users can now access your PathKit portal at{' '}
                      <a
                        href={`https://${company.customDomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-green-700 hover:underline"
                      >
                        https://{company.customDomain}
                      </a>
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        )}

        {error && (
          <Alert className="bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-sm text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {/* Enterprise Feature Info */}
        {isEnterprisePlan && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-start gap-3">
              <Crown className="h-5 w-5 text-purple-600 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-purple-900">Enterprise White-Label Feature</p>
                <p className="text-xs text-purple-700">
                  Custom domains are included with your Enterprise plan. Host PathKit on your own domain for a fully white-labeled experience.
                </p>
                <div className="flex gap-2 text-xs text-purple-600 mt-2">
                  <span>✓ SSL Included</span>
                  <span>•</span>
                  <span>✓ Automatic Renewal</span>
                  <span>•</span>
                  <span>✓ 24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}